import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { toast } from 'react-toastify'
import { useToggleState } from 'lib/hooks/useToggleState'
import { parseLocale } from 'lib/constants/localization'
import { AdditionalSignUpAttributes, getDefaultAdditionalSignUpAttributes } from 'lib/tracking/attribution/signupattributes'
import { trackEvent } from 'lib/tracking/analytics-service'
import { appKnowRoute } from 'lib/constants/routes'
import _useTranslation from 'lib/hooks/_useTranslation'
import theme from 'stories/utils/theme'
import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import DarkThemeImageBannerBody from './DarkThemeImageBannerBody'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import {
    AnalyticsEventKnowConversionDialogButtonClick,
    AnalyticsEventKnowConversionLike,
    AnalyticsEventKnowConversionSave,
    AnalyticsEventKnowConversionShare,
    AnalyticsEventSignupEmailBannerClick,
    AnalyticsEventUserCreate,
    AnalyticsEventWebappSignupDialogSuccessfulSignUp,
} from 'lib/constants/AnalyticsEventType'

interface Props {
    knowSeoInfo: KnowSeoInfo
    onTrigger: (triggerFn: (type?: 'like' | 'save' | 'share', referrerScreen?: string) => void) => void
}

const DarkConversionDialog = ({ knowSeoInfo, onTrigger }: Props) => {
    const [additionalSignUpAttributes, setAdditionalSignUpAttributes] = useState<AdditionalSignUpAttributes>()
    const [showConversionDialog, toggleConversionDialog] = useToggleState(false)

    const router = useRouter()
    const { t: tAuth } = _useTranslation('auth')
    const parsedLocale = parseLocale(router.locale)
    const { know } = knowSeoInfo

    useEffect(() => {
        const attributes = getDefaultAdditionalSignUpAttributes(
            parsedLocale.countryCode,
            parsedLocale.interfaceLanguageCode,
            know.uuid,
            know.universityAuthoredDate ?? null,
            know?.schoolTypes?.[0]?.id ?? null
        )

        setAdditionalSignUpAttributes(attributes)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const conversionDialogButtonClick = (eventReferrerScreen = 'knowPreviewSidebar') => {
        trackEvent(AnalyticsEventKnowConversionDialogButtonClick, {
            know: know ?? undefined,
            useBeacon: true,
            referrerScreen: eventReferrerScreen,
        })

        toggleConversionDialog()
    }

    const handleTrigger = (type?: 'like' | 'save' | 'share', eventReferrerScreen = 'knowPreviewSidebar') => {
        if (type) {
            switch (type) {
                case 'like':
                    trackEvent(AnalyticsEventKnowConversionLike, { know, referrerScreen: eventReferrerScreen })
                    break
                case 'save':
                    trackEvent(AnalyticsEventKnowConversionSave, { know, referrerScreen: eventReferrerScreen })
                    break
                case 'share':
                    trackEvent(AnalyticsEventKnowConversionShare, { know, referrerScreen: eventReferrerScreen })
                    break
            }
        }
        conversionDialogButtonClick(eventReferrerScreen)
    }

    const closeConversionDialog = () => {
        toggleConversionDialog()
    }

    const onSuccessSignUp = (isNewAccount: boolean, type: 'google' | 'apple' | 'email') => {
        if (isNewAccount) {
            trackEvent(AnalyticsEventUserCreate, { value: type, referrerScreen: 'knowPreviewSidebar' })
            toast.success(tAuth('auth/signUpRegisterSuccessMessage'))
        }

        trackEvent(AnalyticsEventWebappSignupDialogSuccessfulSignUp, {
            referrerScreen: 'knowPreviewSidebar',
            know,
            value: 'sidebar',
        })

        router.push(appKnowRoute(know.uuid, { referrerScreen: 'sidebar' }))
    }

    const onEmailButtonClick = () => {
        trackEvent(AnalyticsEventSignupEmailBannerClick, { know, referrerScreen: 'knowPreviewSidebar' })
    }

    // Expose the trigger function to parent
    useEffect(() => {
        onTrigger(handleTrigger)
    }, [onTrigger])

    return (
        <>
            {showConversionDialog ? (
                <FeatureDialog
                    open={showConversionDialog}
                    onClose={closeConversionDialog}
                    hideTitle
                    ariaPrefix={'sidebar'}
                    title={''}
                    fullNoPadding
                    maxWidth={700}
                    noPadding
                    backgroundColor={theme.colors.transparent}
                >
                    <DarkThemeImageBannerBody
                        knowSeoInfo={knowSeoInfo}
                        onSuccessSignup={onSuccessSignUp}
                        additionalSignUpAttributes={additionalSignUpAttributes}
                        index={0}
                        onEmailButtonClick={onEmailButtonClick}
                    />
                </FeatureDialog>
            ) : null}
        </>
    )
}

export default DarkConversionDialog
